/**
 * API Service Index
 */

export { LearningTreeService, type ProblemSpace } from './learning-tree.service';
export { ContentService } from './content.service';
export { ContentBatchService } from './content-batch.service';
export { UserPersonaService } from './user-persona.service';
export { BranchPointService } from './branch-point.service';
export { PublicationService } from './publication.service';
export { NodeService, type CreateChildNodeResult, type CreateChildNodeStatusResult } from './node.service';
export { MediaService } from './media.service';
export { TimelineService } from './timeline.service';
export { ContentDiscoveryService } from './content-discovery.service';
export { WorkflowService } from './workflow.service';
export { ProblemSpaceService, type ProblemSpaceQuestion, type InferResult, type ExtractResult, type AnswerItem } from './problem-space.service';
export { ChatService } from './chat.service';
export {
  AssessmentService,
  type CreateAssessmentInput,
  type CreateAssessmentResult,
  type AssessmentNodeInfo,
  type QuizItemForClient,
  type QuizItemsResponse,
  type SubmitQuizInput,
  type SubmitQuizResult,
  type AttemptHistoryItem,
  type AttemptsResponse,
  type RegenerateResult,
} from './assessment.service';

export type {
  KnowledgeTreeNode,
  KnowledgeTreeResponse,
  UserPersona,
  ImageHighlight,
  RandomHighlightsResponse,
  UserLearningTree,
  TimelineItemData,
  TreeType,
  LearningType,
} from './types';

import { LearningTreeService } from './learning-tree.service';
import { ContentService } from './content.service';
import { ContentBatchService } from './content-batch.service';
import { UserPersonaService } from './user-persona.service';
import { BranchPointService } from './branch-point.service';
import { PublicationService } from './publication.service';
import { NodeService } from './node.service';
import { MediaService } from './media.service';
import { TimelineService } from './timeline.service';
import { ContentDiscoveryService } from './content-discovery.service';
import { WorkflowService } from './workflow.service';
import { ProblemSpaceService } from './problem-space.service';
import { AssessmentService } from './assessment.service';

/**
 *
 * @example
 * import { ApiService } from '@/services/api.service';
 * await ApiService.getLearningTree(id);
 *
 * import { LearningTreeService } from '@/services/api';
 * await LearningTreeService.getLearningTree(id);
 */
export class ApiService {
  private static instance: ApiService;

  private constructor() {}

  static getInstance(): ApiService {
    if (!this.instance) {
      this.instance = new ApiService();
    }
    return this.instance;
  }

  // ==================== Learning Tree ====================
  static getExistingKnowledgeTree = LearningTreeService.getExistingKnowledgeTree.bind(LearningTreeService);
  static getKnowledgeTree = LearningTreeService.getKnowledgeTree.bind(LearningTreeService);
  static createLearningTreeWithAnswers = LearningTreeService.createLearningTreeWithAnswers.bind(LearningTreeService);
  static sendLearningTreeSignal = LearningTreeService.sendLearningTreeSignal.bind(LearningTreeService);
  static createLearningTree = LearningTreeService.createLearningTree.bind(LearningTreeService);
  static getLearningTree = LearningTreeService.getLearningTree.bind(LearningTreeService);
  static getGenerationStatus = LearningTreeService.getGenerationStatus.bind(LearningTreeService);
  static getUserLearningTrees = LearningTreeService.getUserLearningTrees.bind(LearningTreeService);
  static getUserLearningTreeCounts = LearningTreeService.getUserLearningTreeCounts.bind(LearningTreeService);
  static searchLearningTrees = LearningTreeService.searchLearningTrees.bind(LearningTreeService);

  // ==================== Content ====================
  static generateLLMContent = ContentService.generateLLMContent.bind(ContentService);
  static getLatestLLMContent = ContentService.getLatestLLMContent.bind(ContentService);
  static getUserContent = ContentService.getUserContent.bind(ContentService);
  static updateUserContentSegments = ContentService.updateUserContentSegments.bind(ContentService);
  static controlContentBatch = ContentService.controlContentBatch.bind(ContentService);
  static getNodeBatchStatus = ContentService.getNodeBatchStatus.bind(ContentService);
  static getTreeBatchStatus = ContentService.getTreeBatchStatus.bind(ContentService);
  static getStyleStatus = ContentService.getStyleStatus.bind(ContentService);
  static generateStyleContent = ContentService.generateStyleContent.bind(ContentService);

  // ==================== User Persona ====================
  static getUserPersona = UserPersonaService.getUserPersona.bind(UserPersonaService);
  static createUserPersona = UserPersonaService.createUserPersona.bind(UserPersonaService);
  static updateUserPersona = UserPersonaService.updateUserPersona.bind(UserPersonaService);
  static deleteUserPersona = UserPersonaService.deleteUserPersona.bind(UserPersonaService);

  // ==================== Branch Point ====================
  static initBranchPoint = BranchPointService.initBranchPoint.bind(BranchPointService);
  static regenerateBranchOptions = BranchPointService.regenerateBranchOptions.bind(BranchPointService);
  static confirmBranchSelection = BranchPointService.confirmBranchSelection.bind(BranchPointService);
  static getBranchPointStatus = BranchPointService.getBranchPointStatus.bind(BranchPointService);
  static generateBranchOptions = BranchPointService.generateBranchOptions.bind(BranchPointService);

  // ==================== Publication ====================
  static createPublication = PublicationService.createPublication.bind(PublicationService);
  static getPublications = PublicationService.getPublications.bind(PublicationService);
  static getPublication = PublicationService.getPublication.bind(PublicationService);
  static updateRevision = PublicationService.updateRevision.bind(PublicationService);
  static publishRevision = PublicationService.publishRevision.bind(PublicationService);
  static createRevision = PublicationService.createRevision.bind(PublicationService);

  // ==================== Node ====================
  static exploreSubKnowledge = NodeService.exploreSubKnowledge.bind(NodeService);
  static deleteTopicNode = NodeService.deleteTopicNode.bind(NodeService);
  static updateNodePositions = NodeService.updateNodePositions.bind(NodeService);
  static resetNodePositions = NodeService.resetNodePositions.bind(NodeService);
  static detachStepNode = NodeService.detachStepNode.bind(NodeService);
  static attachStepNode = NodeService.attachStepNode.bind(NodeService);
  static createChildFromSelection = NodeService.createChildFromSelection.bind(NodeService);
  static createChildFromSelectionAsync = NodeService.createChildFromSelectionAsync.bind(NodeService);
  static getCreateChildNodeStatus = NodeService.getCreateChildNodeStatus.bind(NodeService);


  // ==================== Media ====================
  static uploadCoverImage = MediaService.uploadCoverImage.bind(MediaService);
  static uploadProfileImage = MediaService.uploadProfileImage.bind(MediaService);

  // ==================== Timeline ====================
  static getTimeline = TimelineService.getTimeline.bind(TimelineService);
  static getTimelineStats = TimelineService.getTimelineStats.bind(TimelineService);

  // ==================== Content Discovery ====================
  static getRandomHighlights = ContentDiscoveryService.getRandomHighlights.bind(ContentDiscoveryService);

  // ==================== Workflow ====================
  static getWorkflowStatus = WorkflowService.getWorkflowStatus.bind(WorkflowService);

  // ==================== Content Batch ====================
  static createContentBatch = ContentBatchService.createBatch.bind(ContentBatchService);
  static listContentBatches = ContentBatchService.listBatches.bind(ContentBatchService);
  static getContentBatch = ContentBatchService.getBatch.bind(ContentBatchService);
  static cancelContentBatch = ContentBatchService.cancelBatch.bind(ContentBatchService);

  // ==================== Assessment ====================
  static createAssessment = AssessmentService.createAssessment.bind(AssessmentService);
  static getAssessment = AssessmentService.getAssessment.bind(AssessmentService);
  static getQuizItems = AssessmentService.getQuizItems.bind(AssessmentService);
  static submitQuiz = AssessmentService.submitQuiz.bind(AssessmentService);
  static getAttempts = AssessmentService.getAttempts.bind(AssessmentService);
  static regenerateAssessment = AssessmentService.regenerateAssessment.bind(AssessmentService);

  static CoverImageAttribution: {
    coverAuthor?: string;
    coverAuthorUrl?: string;
    coverSource?: string;
    coverSourceUrl?: string;
  };
}
